/**
 * Time Formatting Utilities
 * Shared utilities for formatting timestamps across components
 */

import logger from './logger';

/**
 * Formats a relative time string from ISO timestamp
 *
 * @param isoString - ISO 8601 timestamp string
 * @returns Human-readable relative time (e.g., "2 hours ago", "3 days ago")
 *
 * @example
 * ```ts
 * formatRelativeTime('2024-01-15T10:30:00Z')
 * // Returns: "2 hours ago" (if current time is 2024-01-15T12:30:00Z)
 * ```
 */
export function formatRelativeTime(isoString: string): string {
  try {
    const date = new Date(isoString);

    // Check if date is valid (Invalid Date returns NaN from getTime())
    if (isNaN(date.getTime())) {
      logger.warn('TimeUtils', 'Invalid timestamp:', isoString);
      return 'Unknown date';
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    const diffWeek = Math.floor(diffDay / 7);
    const diffMonth = Math.floor(diffDay / 30);
    const diffYear = Math.floor(diffDay / 365);

    if (diffYear > 0) return `${diffYear} year${diffYear > 1 ? 's' : ''} ago`;
    if (diffMonth > 0) return `${diffMonth} month${diffMonth > 1 ? 's' : ''} ago`;
    if (diffWeek > 0) return `${diffWeek} week${diffWeek > 1 ? 's' : ''} ago`;
    if (diffDay > 0) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
    if (diffHour > 0) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
    if (diffMin > 0) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
    return 'Just now';
  } catch (error) {
    logger.warn('TimeUtils', 'Error formatting timestamp:', isoString, error);
    return 'Unknown date';
  }
}
